import React, { useState, useEffect, useRef } from 'react';
import { FileText, Eye, Download, User, Phone, MessageCircle, Search, Calendar, X, ChevronDown, Users, Package, ArrowLeft, Filter, FileSpreadsheet, Archive, Mail, Save, CheckCircle, AlertCircle, Briefcase } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { BASE_URL , updateBranchManager } from './api';
import { handleSmartReportGenerate } from './medivadiReport/english';
import { handleMarathiSmartReportGenerate } from './medivadiReport/Marathi';
import { handletelaguSmartReportGenerate } from './medivadiReport/Telegu';
import { handlegujrathiSmartReportGenerate } from './medivadiReport/Gujrati';
import { handleSmartReportBangali } from './medivadiReport/bangali';
import { handleSmartReportGenerateOdiya } from './medivadiReport/odiya';

const MedibuddyPatients = () => {
  const { campId: urlCampId } = useParams();
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDocuments, setPatientDocuments] = useState({}); 
  const [searchTerm, setSearchTerm] = useState('');
  const [campId, setCampId] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [selectedDate, setSelectedDate] = useState('');
  const dateInputRef = useRef(null);
  const [downloadCount, setDownloadCount] = useState(0);
  const [isDownloadingReports, setIsDownloadingReports] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);
  const API_BASE_URL = BASE_URL;
  
  // --- Branch Manager: new state (does not touch any existing state) ---
  const [campDetails, setCampDetails] = useState(null);
  const [branchManagerName, setBranchManagerName] = useState('');
  const [branchManagerEmail, setBranchManagerEmail] = useState('');
  const [isSavingBranchManager, setIsSavingBranchManager] = useState(false);
  const [branchManagerError, setBranchManagerError] = useState('');
  const [branchManagerSuccess, setBranchManagerSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!urlCampId) return;
      try {
        setCampId(urlCampId);
        const response = await fetch(`${API_BASE_URL}/api/campmanager/camps/${urlCampId}/details/`);
        if (!response.ok) throw new Error('Failed to fetch camp details');
        const data = await response.json();
        const packages = data.packages || [];
        if (packages.length === 0) {
          setError('No packages found for this camp.');
          return;
        }
        const pkgId = packages[0].id;
        setPackageId(pkgId.toString());
        const currentPackage = packages.find((pkg) => pkg.id === pkgId);
        const fetchedLanguage = currentPackage?.report_language?.trim() || 'English';
        setSelectedLanguage(fetchedLanguage);
        localStorage.setItem('selectedLanguage', fetchedLanguage);

        // --- Branch Manager: hydrate from the camp details response we already fetched.
        // Supports either a nested `data.camp` object or fields sitting on `data` directly,
        // so we never issue a second request just to read these two fields (req #17).
        const camp = data.camp || data;
        setCampDetails(camp);
        setBranchManagerName(camp?.branch_manager_name || '');
        setBranchManagerEmail(camp?.branch_manager_email || '');
      } catch (err) {
        console.error('Error fetching camp details:', err);
        setError('Failed to load camp details.');
      }
    };
    fetchData();
  }, [urlCampId]);

  useEffect(() => {
    localStorage.setItem('selectedLanguage', selectedLanguage);
  }, [selectedLanguage]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) setSelectedLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    if (campId && packageId) fetchPatients();
  }, [campId, packageId]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/campmanager/patients/filter/?camp_id=${campId}&package_id=${packageId}`);
      if (!response.ok) throw new Error('Failed to fetch patients');
      const data = await response.json();
      setPatients(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredPatients = patients.filter(p => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      p.name.toLowerCase().includes(searchLower) ||
      p.unique_patient_id.toLowerCase().includes(searchLower) ||
      p.phone.includes(searchTerm) ||
      (p.email && p.email.toLowerCase().includes(searchLower)) ||
      (p.patient_id && p.patient_id.toLowerCase().includes(searchLower));
    const matchesDate = !selectedDate || p.test_date === selectedDate;
    return matchesSearch && matchesDate;
  });

  const fetchPatientDocuments = async (unique_patient_id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/campmanager/patient-documents/${unique_patient_id}/`);
      if (!response.ok) throw new Error('Failed to fetch documents');
      const data = await response.json();
      if (data && data.length > 0) {
        const doc = data[0];
        return {
          photo: doc.patient_photo ? { url: doc.patient_photo, name: 'Patient Photo' } : null,
          aadhar: doc.document_file ? { url: doc.document_file, name: 'Aadhar Document' } : null,
          loan: doc.loan_document_file ? { url: doc.loan_document_file, name: 'Loan Document' } : null,
        };
      }
      return null;
    } catch (err) {
      console.error('Fetch document error:', err);
      return null;
    }
  };

  const handleView = async (patient) => {
    setSelectedPatient(patient);
    setShowViewModal(true);
    const documents = await fetchPatientDocuments(patient.unique_patient_id);
    if (documents) {
      setPatientDocuments((prev) => ({ ...prev, [patient.id]: documents }));
    }
  };

  const handleReport = async (patient) => {
    const response = await fetch(
      `${API_BASE_URL}/api/technician/smart-report/download/?patient_ids=${patient.unique_patient_id}`,
      { method: 'GET' }
    );
    return response;
  };

  const handleSingleReportDownload = async (patient) => {
    try {
      const response = await handleReport(patient);
      if (response.status === 200) {
        await downloadPdfFromResponse(response, patient);
      } else if (response.status === 404) {
        await getSmartReportHandler()(patient, BASE_URL, API_BASE_URL);
        const retryResponse = await handleReport(patient);
        if (retryResponse.status === 200) {
          await downloadPdfFromResponse(retryResponse, patient);
        } else {
          alert('Failed to download report');
        }
      } else {
        alert(`Server error (${response.status})`);
      }
      await fetchPatients();
    } catch (error) {
      console.error('Download error:', error);
      alert(`Error downloading report: ${error.message}`);
    }
  };

  const downloadPdfFromResponse = async (response, patient) => {
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = patient.name ? patient.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Patient';
    a.href = url;
    a.download = `Smart_Report_${safeName}_${patient.unique_patient_id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getSmartReportHandler = () => {
    switch (selectedLanguage) {
      case 'Marathi': return handleMarathiSmartReportGenerate;
      case 'Telegu': return handletelaguSmartReportGenerate;
      case 'Gujarati': return handlegujrathiSmartReportGenerate;
      case 'Bengali': return handleSmartReportBangali;
      case 'Odiya': return handleSmartReportGenerateOdiya;
      default: return handleSmartReportGenerate;
    }
  };

  const handleGenerateSmartReport = async (patient) => {
    try {
      await getSmartReportHandler()(patient, BASE_URL, API_BASE_URL);
      await fetchPatients();
    } catch (err) {
      console.error('Smart report generation error:', err);
    }
  };

  const handleBulkSmartReportDownload = async () => {
    if (selectedPatients.length === 0) {
      alert('Please select at least one patient');
      return;
    }
    setIsDownloadingReports(true);
    setDownloadCount(0);
    let successCount = 0;
    const errors = [];
    for (const patientId of selectedPatients) {
      const patient = patients.find(p => p.unique_patient_id === patientId);
      if (!patient) continue;
      try {
        const response = await handleReport(patient);
        if (response.status === 200) {
          await downloadPdfFromResponse(response, patient);
          successCount++;
          setDownloadCount(successCount);
        } else if (response.status === 404) {
          await getSmartReportHandler()(patient, BASE_URL, API_BASE_URL);
          const retryResponse = await handleReport(patient);
          if (retryResponse.status === 200) {
            await downloadPdfFromResponse(retryResponse, patient);
            successCount++;
            setDownloadCount(successCount);
          } else {
            throw new Error('Download failed after generation');
          }
        } else {
          throw new Error(`Server error (${response.status})`);
        }
      } catch (err) {
        errors.push(`${patient.name || 'Patient'} (${patient.unique_patient_id}): ${err.message}`);
      }
    }
    await fetchPatients();
    setIsDownloadingReports(false);
    if (errors.length > 0) {
      alert('Some smart reports could not be processed:\n\n' + errors.join('\n'));
    }
  };

  const handleWhatsApp = async (patient) => {
    try {
      const response = await fetch('https://cbackend.xraidigital.com/api/technician/send-whatsapp/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: parseInt(patient.id),
          remarks: `Smart Report PDF for ${patient.name} (ID: ${patient.unique_patient_id})`,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send WhatsApp message');
      }
      await response.json();
      alert('WhatsApp message sent successfully!');
    } catch (error) {
      console.error('WhatsApp error:', error);
      alert(`Error sending WhatsApp message: ${error.message}`);
    }
  };

  const handleBulkWhatsApp = async () => {
    if (selectedPatients.length === 0) {
      alert('Please select at least one patient');
      return;
    }
    try {
      for (const patientId of selectedPatients) {
        const patient = patients.find(p => p.unique_patient_id === patientId);
        if (!patient) continue;
        await fetch('https://cbackend.xraidigital.com/api/technician/send-whatsapp/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patient_id: parseInt(patient.id),
            remarks: `Smart Report PDF for ${patient.name} (ID: ${patient.unique_patient_id})`,
          }),
        });
      }
      alert('WhatsApp messages sent to selected patients!');
    } catch (error) {
      console.error('Bulk WhatsApp error:', error);
      alert('Error sending bulk WhatsApp messages');
    }
  };

  const handleSMS = async (patient) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/technician/send-sms/${patient.id}/`, { method: 'GET' });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to send SMS');
      alert('SMS sent successfully!');
    } catch (error) {
      console.error('SMS error:', error);
      alert(`Error sending SMS: ${error.message}`);
    }
  };

  const handleBulkSMS = async () => {
    if (selectedPatients.length === 0) {
      alert('Please select at least one patient');
      return;
    }
    try {
      for (const patientId of selectedPatients) {
        const patient = patients.find(p => p.unique_patient_id === patientId);
        if (!patient) continue;
        await fetch(`${API_BASE_URL}/api/campmanager/send-sms/${patient.id}/`, { method: 'GET' });
      }
      alert('SMS sent to selected patients!');
    } catch (error) {
      console.error('Bulk SMS error:', error);
      alert('Error sending bulk SMS');
    }
  };

  const handleDownloadWhatsAppLogs = async () => {
    try {
      const response = await fetch(`https://cbackend.xraidigital.com/api/technician/export-whatsapp-logs/${campId}/`, {
        method: 'GET',
      });
      if (!response.ok) throw new Error('Failed to download WhatsApp logs');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'whatsapp_logs.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download WhatsApp logs error:', error);
      alert(`Error downloading WhatsApp logs: ${error.message}`);
    }
  };

  const handleSendEmailToVendor = async () => {
    try {
      if (!campId) {
        alert('Camp ID is not available.');
        return;
      }
      const response = await fetch(`https://cbackend.xraidigital.com/api/camp/${campId}/send-report-email/`, {
        method: 'POST',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to send email (${response.status})`);
      }
      const data = await response.json().catch(() => null);
      alert(data?.message || 'Email sent successfully!');
    } catch (error) {
      console.error('Send email error:', error);
      alert(`Error sending email: ${error.message}`);
    }
  };

  const handleBulkDownloadZip = async () => {
    if (selectedPatients.length === 0) {
      alert('Please select at least one patient');
      return;
    }
    setIsZipping(true);
    setZipProgress(0);
    const zip = new JSZip();
    let successCount = 0;
    const errors = [];
    for (let i = 0; i < selectedPatients.length; i++) {
      const patientId = selectedPatients[i];
      const patient = patients.find(p => p.unique_patient_id === patientId);
      if (!patient) continue;
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/technician/smart-report/download/?patient_ids=${patient.unique_patient_id}`,
          { method: 'GET' }
        );
        if (response.status === 200) {
          const blob = await response.blob();
          const safeName = patient.name ? patient.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Patient';
          zip.file(`Smart_Report_${safeName}_${patient.unique_patient_id}.pdf`, blob);
          successCount++;
        } else if (response.status === 404) {
          await getSmartReportHandler()(patient, BASE_URL, API_BASE_URL);
          const retryResponse = await fetch(
            `${API_BASE_URL}/api/technician/smart-report/download/?patient_ids=${patient.unique_patient_id}`,
            { method: 'GET' }
          );
          if (retryResponse.status === 200) {
            const blob = await retryResponse.blob();
            const safeName = patient.name ? patient.name.replace(/[^a-zA-Z0-9]/g, '_') : 'Patient';
            zip.file(`Smart_Report_${safeName}_${patient.unique_patient_id}.pdf`, blob);
            successCount++;
          } else {
            errors.push(`${patient.name || 'Patient'}: Failed after generation`);
          }
        } else {
          errors.push(`${patient.name || 'Patient'}: Server error (${response.status})`);
        }
      } catch (err) {
        errors.push(`${patient.name || 'Patient'}: ${err.message}`);
      }
      setZipProgress(Math.round(((i + 1) / selectedPatients.length) * 100));
    }
    await fetchPatients();
    if (successCount > 0) {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Smart_Reports_${campId}.zip`);
    }
    setIsZipping(false);
    setZipProgress(0);
    if (errors.length > 0) {
      alert(`Some reports failed:\n\n${errors.join('\n')}`);
    }
  };

  // --- Branch Manager: validation + save handler ---
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleUpdateBranchManager = async () => {
    setBranchManagerError('');
    setBranchManagerSuccess('');

    const trimmedName = branchManagerName.trim();
    const trimmedEmail = branchManagerEmail.trim();

    if (!trimmedName) {
      setBranchManagerError('Branch Manager Name is required.');
      return;
    }
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setBranchManagerError('Please enter a valid Branch Manager Email address.');
      return;
    }

    setIsSavingBranchManager(true);
    try {
      const result = await updateBranchManager(campId, trimmedName, trimmedEmail);

      // Update local state immediately from the response, no full reload/refetch needed
      setBranchManagerName(result.branch_manager_name ?? trimmedName);
      setBranchManagerEmail(result.branch_manager_email ?? trimmedEmail);
      setCampDetails((prev) => (prev
        ? { ...prev, branch_manager_name: result.branch_manager_name, branch_manager_email: result.branch_manager_email }
        : prev));
      setBranchManagerSuccess(result.message || 'Branch manager details updated successfully.');
    } catch (err) {
      console.error('Update branch manager error:', err);
      setBranchManagerError(err.message || 'Failed to update branch manager details.');
      // Values are intentionally left as-is in the inputs (not cleared) per requirement.
    } finally {
      setIsSavingBranchManager(false);
    }
  };

  const smartReportCount = patients.filter(p => p.smart_report).length;
  const hasSmartReport = (patient) => Boolean(patient.smart_report);

  const allPatientIds = filteredPatients.map(p => p.unique_patient_id);
  const isAllSelected =
    filteredPatients.length > 0 &&
    filteredPatients.every(p => selectedPatients.includes(p.unique_patient_id));

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return 'bg-gray-400';
    const colors = [
      'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-orange-500',
      'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          .skeleton { background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; }
        `}</style>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl skeleton" />
            <div>
              <div className="h-8 w-64 rounded skeleton mb-2" />
              <div className="h-4 w-40 rounded skeleton" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="h-4 w-24 rounded skeleton mb-3" />
                <div className="h-8 w-16 rounded skeleton" />
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <div className="flex gap-4">
              <div className="h-12 flex-1 rounded-lg skeleton" />
              <div className="h-12 w-40 rounded-lg skeleton" />
              <div className="h-12 w-32 rounded-lg skeleton" />
            </div>
          </div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-3">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full skeleton" />
                <div className="flex-1">
                  <div className="h-5 w-48 rounded skeleton mb-2" />
                  <div className="h-4 w-32 rounded skeleton mb-1" />
                  <div className="h-3 w-24 rounded skeleton" />
                </div>
                <div className="flex gap-2">
                  <div className="h-9 w-24 rounded-lg skeleton" />
                  <div className="h-9 w-24 rounded-lg skeleton" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center px-4">
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate(`/download-reports/${urlCampId}`, { replace: true })}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-medium transition-all shadow-lg shadow-blue-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .patient-row:hover { transform: translateX(2px); }
      `}</style>

      {/* Gradient Header */}
      <div className="bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Medibuddy Package</h1>
                <div className="flex items-center gap-3 mt-1 text-teal-100 text-sm">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-300 rounded-full inline-block" />
                    Camp #{campId}
                  </span>
                  <span className="w-1 h-1 bg-white/40 rounded-full" />
                  <span>Package #{packageId}</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate(`/download-reports/${urlCampId}`, { replace: true })}
              className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur text-white px-5 py-2.5 rounded-xl font-medium transition-all border border-white/20"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Reports
            </button>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3 border border-white/10">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-teal-200" />
                <div>
                  <p className="text-teal-100 text-xs font-medium">Total Patients</p>
                  <p className="text-xl font-bold">{patients.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3 border border-white/10">
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-teal-200" />
                <div>
                  <p className="text-teal-100 text-xs font-medium">Showing</p>
                  <p className="text-xl font-bold">{filteredPatients.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3 border border-white/10">
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-teal-200" />
                <div>
                  <p className="text-teal-100 text-xs font-medium">Selected</p>
                  <p className="text-xl font-bold">{selectedPatients.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl px-5 py-3 border border-white/10">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-green-200" />
                <div>
                  <p className="text-teal-100 text-xs font-medium">Smart Reports Done</p>
                  <p className="text-xl font-bold">{smartReportCount}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        
        {/* Branch Manager Card (new) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-teal-50 text-teal-600 rounded-lg flex items-center justify-center">
              <Briefcase className="w-4 h-4" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Branch Manager Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Branch Manager Name</label>
              <input
                type="text"
                value={branchManagerName}
                onChange={(e) => setBranchManagerName(e.target.value)}
                placeholder="e.g. Rahul Sharma"
                disabled={isSavingBranchManager}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400 disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Branch Manager Email</label>
              <input
                type="email"
                value={branchManagerEmail}
                onChange={(e) => setBranchManagerEmail(e.target.value)}
                placeholder="e.g. rahul@example.com"
                disabled={isSavingBranchManager}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400 disabled:opacity-60"
              />
            </div>
          </div>

          {branchManagerError && (
            <div className="mt-3 flex items-start gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{branchManagerError}</span>
            </div>
          )}
          {branchManagerSuccess && (
            <div className="mt-3 flex items-start gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{branchManagerSuccess}</span>
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={handleUpdateBranchManager}
              disabled={isSavingBranchManager}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isSavingBranchManager ? (
                <>
                  <svg className="w-4 h-4 animate-spin" style={{ animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Branch Manager
                </>
              )}
            </button>
          </div>
        </div>
        {/* Action Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, ID, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all placeholder:text-gray-400"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Date Filter */}
            <div className="relative">
              <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
              />
            </div>

            {selectedDate && (
              <button
                onClick={() => setSelectedDate('')}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-xl text-sm font-medium hover:bg-orange-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}

            {/* Language */}
            <div className="relative">
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="appearance-none pl-4 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
              >
                <option value="English">English</option>
                <option value="Bengali">Bengali</option>
                <option value="Gujarati">Gujarati</option>
                <option value="Telegu">Telegu</option>
                <option value="Marathi">Marathi</option>
                <option value="Odiya">Odiya</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Bulk Action Buttons */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={handleBulkSmartReportDownload}
              disabled={isDownloadingReports || selectedPatients.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <Download className="w-4 h-4" />
              {isDownloadingReports
                ? `Downloading... (${downloadCount}/${selectedPatients.length})`
                : `Smart Reports${selectedPatients.length > 0 ? ` (${selectedPatients.length})` : ''}`}
            </button>

            <button
              onClick={handleBulkWhatsApp}
              disabled={selectedPatients.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <MessageCircle className="w-4 h-4" />
              Bulk WhatsApp
            </button>

            <button
              onClick={handleBulkSMS}
              disabled={selectedPatients.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <Phone className="w-4 h-4" />
              Bulk SMS
            </button>

            <button
              onClick={handleDownloadWhatsAppLogs}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
            >
              <FileSpreadsheet className="w-4 h-4" />
              WhatsApp Logs
            </button>

            <button
              onClick={handleBulkDownloadZip}
              disabled={isZipping || selectedPatients.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <Archive className="w-4 h-4" />
              {isZipping ? `Zipping... (${zipProgress}%)` : `Download ZIP${selectedPatients.length > 0 ? ` (${selectedPatients.length})` : ''}`}
            </button>

            <button
              onClick={handleSendEmailToVendor}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-700 hover:to-rose-800 text-white rounded-xl text-sm font-medium transition-all shadow-sm"
            >
              <Mail className="w-4 h-4" />
              Send Email to Vendor
            </button>

            {selectedPatients.length > 0 && (
              <button
                onClick={() => setSelectedPatients([])}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors ml-auto"
              >
                <X className="w-3.5 h-3.5" />
                Clear Selection ({selectedPatients.length})
              </button>
            )}
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100">
                <th className="px-5 py-4 text-left">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={(e) => setSelectedPatients(e.target.checked ? allPatientIds : [])}
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {selectedPatients.length > 0 ? `${selectedPatients.length} selected` : 'Select all'}
                    </span>
                  </div>
                </th>
                <th className="px-5 py-4 text-left">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Patient Info</span>
                </th>
                <th className="px-5 py-4 text-right">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredPatients.map((patient, idx) => {
                const isSelected = selectedPatients.includes(patient.unique_patient_id);
                return (
                  <tr
                    key={patient.id}
                    className={`group transition-all duration-150 patient-row ${
                      isSelected
                        ? 'bg-teal-50/60 border-l-3 border-l-teal-500'
                        : hasSmartReport(patient)
                          ? 'bg-green-50 hover:bg-green-100/70'
                          : 'hover:bg-gray-50/60'
                    }`}
                  >
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPatients((prev) => [...prev, patient.unique_patient_id]);
                          } else {
                            setSelectedPatients((prev) => prev.filter((id) => id !== patient.unique_patient_id));
                          }
                        }}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-4">
                        <div className={`flex-shrink-0 w-11 h-11 rounded-xl ${getAvatarColor(patient.name)} flex items-center justify-center text-white text-sm font-bold shadow-sm`}>
                          {getInitials(patient.name)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm truncate">{patient.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-600">
                              {patient.unique_patient_id}
                            </span>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs text-gray-500">{patient.age}y, {patient.gender}</span>
                            <span className="text-gray-300">|</span>
                            <span className="text-xs text-gray-500">{patient.phone}</span>
                          </div>
                          {patient.test_date && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-teal-600 mt-1">
                              <Calendar className="w-3 h-3" />
                              {patient.test_date}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleGenerateSmartReport(patient)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-lg text-xs font-medium transition-colors"
                          title="Generate Smart Report"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Smart Report
                        </button>
                        <button
                          onClick={() => handleSingleReportDownload(patient)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 rounded-lg text-xs font-medium transition-colors"
                          title="Download Report"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </button>
                        <button
                          onClick={() => handleWhatsApp(patient)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-xs font-medium transition-colors"
                          title="Send via WhatsApp"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          WhatsApp
                        </button>
                        <button
                          onClick={() => handleView(patient)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs font-medium transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-3">
          {filteredPatients.map((patient) => {
            const isSelected = selectedPatients.includes(patient.unique_patient_id);
            return (
              <div
                key={patient.id}
                className={`rounded-2xl shadow-sm border overflow-hidden transition-all ${
                  isSelected
                    ? 'bg-teal-50/60 border-teal-400 ring-2 ring-teal-100'
                    : hasSmartReport(patient)
                      ? 'bg-green-50 border-green-200'
                      : 'bg-white border-gray-100'
                }`}
              >
                {/* Colored top border */}
                <div className={`h-1 ${
                  isSelected
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-500'
                    : hasSmartReport(patient)
                      ? 'bg-gradient-to-r from-green-400 to-emerald-400'
                      : 'bg-gradient-to-r from-gray-200 to-gray-300'
                }`} />

                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPatients((prev) => [...prev, patient.unique_patient_id]);
                        } else {
                          setSelectedPatients((prev) => prev.filter((id) => id !== patient.unique_patient_id));
                        }
                      }}
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${getAvatarColor(patient.name)} flex items-center justify-center text-white text-sm font-bold`}>
                      {getInitials(patient.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{patient.name}</h3>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-600">
                          {patient.unique_patient_id}
                        </span>
                        <span className="text-xs text-gray-400">{patient.age}y, {patient.gender}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{patient.phone}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleGenerateSmartReport(patient)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-xl text-xs font-medium transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Smart Report
                    </button>
                    <button
                      onClick={() => handleSingleReportDownload(patient)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200 rounded-xl text-xs font-medium transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      onClick={() => handleWhatsApp(patient)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-xs font-medium transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp
                    </button>
                    <button
                      onClick={() => handleView(patient)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-xl text-xs font-medium transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View Detail
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredPatients.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 py-16 px-6 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Users className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No patients found</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              {patients.length > 0
                ? 'Try adjusting your search or filter criteria to find patients.'
                : 'No patients are registered for this package yet.'}
            </p>
            {(searchTerm || selectedDate) && (
              <button
                onClick={() => { setSearchTerm(''); setSelectedDate(''); }}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-xl text-sm font-medium transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* View Detail Modal */}
      {showViewModal && selectedPatient && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-600 to-emerald-500 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">{selectedPatient.name}</h2>
                    <p className="text-teal-100 text-sm">{selectedPatient.unique_patient_id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-8 h-8 bg-white/15 hover:bg-white/25 rounded-lg flex items-center justify-center transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
              {/* Patient Info Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Age</p>
                  <p className="text-lg font-bold text-gray-900 mt-0.5">{selectedPatient.age}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">Gender</p>
                  <p className="text-lg font-bold text-gray-900 mt-0.5">{selectedPatient.gender}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <div className="flex items-center justify-center gap-1 text-gray-400 mb-0.5">
                    <Phone className="w-3 h-3" />
                    <p className="text-[11px] font-medium uppercase tracking-wider">Phone</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{selectedPatient.phone}</p>
                </div>
                {selectedPatient.test_date && (
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-gray-400 mb-0.5">
                      <Calendar className="w-3 h-3" />
                      <p className="text-[11px] font-medium uppercase tracking-wider">Test Date</p>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{selectedPatient.test_date}</p>
                  </div>
                )}
              </div>

              {/* Documents Section */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  Documents
                </h3>
                <div className="space-y-2">
                  {['photo', 'aadhar', 'loan'].map((docType) => {
                    const doc = patientDocuments[selectedPatient.id]?.[docType];
                    const labels = { photo: 'Patient Photo', aadhar: 'Aadhar ID Document', loan: 'Loan ID Document' };
                    return (
                      <div key={docType} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${doc ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${doc ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'}`}>
                            {docType === 'photo' ? <User className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                          </div>
                          <span className="text-sm font-medium text-gray-700">{labels[docType]}</span>
                        </div>
                        {doc ? (
                          <div className="flex items-center gap-2">
                            <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-medium transition-colors">
                              <Eye className="w-3 h-3" />
                              View
                            </a>
                            <a href={doc.url} download={doc.name} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg text-xs font-medium transition-colors">
                              <Download className="w-3 h-3" />
                              Download
                            </a>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Not uploaded</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Services */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Services</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedPatient.services.map((service, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-medium">
                      {service}
                    </span>
                  ))}
                </div>
              </div>

              {/* Download Button */}
              <button
                onClick={() => handleSingleReportDownload(selectedPatient)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl font-medium transition-all shadow-lg shadow-purple-200"
              >
                <Download className="w-4 h-4" />
                Download Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedibuddyPatients;
